<script lang="ts">
	import type { FinancialNumber } from 'shared/number'

	import { image_root, background_texture_pathname } from 'shared/bzns/holster/image_paths'

	import SaleFlash from './SaleFlash.svelte'
	import LeadTime from './LeadTime.svelte'
	import GracefullySwitchingImage from './GracefullySwitchingImage.svelte'

	export let holster_line: Pick<TrexHolsterLine,
		`name` |
		`image_file_name_string` |
		`image_position_algorithm` |
		`product_category_image_path`
	>

	export let product_image_alt: string | null = null
	export let product_image_file_name: string | null = null

	export let attachment_image_alt: string | null = null
	export let attachment_image_file_name: string | null = null

	export let flip_x = false

	export let bottom_overlay_text = ``
	export let promo_discount_rate: FinancialNumber
	export let promo_active: boolean
	export let lead_time_text: string
	export let is_accessory = false

	const imgix_image_width = 800
	const dpr = typeof window !== `undefined` ? window.devicePixelRatio : 2
	const imgix_args = `?auto=format,compress&w=${ imgix_image_width }&dpr=${ dpr }`

	const get_full_image_path = (image_file_name: string) => `${ image_root }${ holster_line.image_file_name_string }${ image_file_name }${ imgix_args }`

	$: product_image_src = product_image_file_name && get_full_image_path(product_image_file_name)

	$: attached_product_image_src = attachment_image_file_name && get_full_image_path(attachment_image_file_name)

	const z_index_style = holster_line.image_position_algorithm === `1`
		? ``
		: `z-index: -10;`

	const accessory_x_offset_style = holster_line.image_position_algorithm === `1` && is_accessory
		? flip_x ? `left: 22%;` : `right: 22%;` : ``

	const q = dpr > 1 ? 20 : 60 // high dpr looks OK with lower quality
	const background_texture_image_src = `${ background_texture_pathname }?$w=512&h=410&fit=crop&q=${ q }&dpr=${ dpr }`

</script>

<div
	class="product_images_container"
	data-flip-x={flip_x}
>
	<img
		alt={holster_line.name}
		class="image_for_google_to_read"
		src="https://img.trex-arms.com/{ holster_line.product_category_image_path }?w=400&auto=format,compress"
		role=presentation
	>
	<img
		alt="Background texture"
		class="background_texture"
		src={background_texture_image_src}
		role=presentation
	>
	{#if promo_active && Number(promo_discount_rate) > 0}
		<div class="sale_flash">
			<SaleFlash {promo_discount_rate}></SaleFlash>
		</div>
	{/if}

	<div class="lead_time">
		<LeadTime {lead_time_text} />
	</div>

	<span class=bottom_overlay_text>{bottom_overlay_text}</span>
	<div class=product_and_attachment_images_wrapper>
		{#if product_image_src}
			<GracefullySwitchingImage
				alt={product_image_alt || `Placeholder holster`}
				src={product_image_src}
				style={accessory_x_offset_style}
			/>
		{/if}
		{#if attached_product_image_src}
			<GracefullySwitchingImage
				alt={attachment_image_alt || `Placeholder attachment`}
				src={attached_product_image_src}
				style={z_index_style}
			/>
		{/if}
	</div>
</div>

<style>
	.product_images_container {
		position: relative;
		background-color: var(--better-contrast-background);
		padding: 20px;
		min-width: 250px;

		display: flex;
		overflow: hidden;
	}

	.sale_flash {
		position: absolute;
		top: 0;
		left: 0;
		--font-size: 36px;
		--mobile-font-size: 6vw;
	}

	.lead_time {
		position: absolute;
		top: 0;
		right: 0;
	}

	.product_and_attachment_images_wrapper {
		aspect-ratio: 1;
		margin-bottom: -10%;
		width: 100%;
		transform: scaleX(1);
		filter: drop-shadow(20px 10px 10px #0006) drop-shadow(0 0 50px #000);
		transform: translateZ(0); /* Fixes a Safari drop shadow rendering bug: https://github.com/mdn/browser-compat-data/issues/17726#issuecomment-1286324855 */
	}

	[data-flip-x=true] .product_and_attachment_images_wrapper {
		transform: scaleX(-1);
	}

	@media (min-width: 850px) {
		.product_and_attachment_images_wrapper {
			margin-bottom: 0;
		}
	}

	.bottom_overlay_text {
		position: absolute;
		bottom: 0;
		left: 0;
		padding: 4px 8px;
		font-size: 16px;
		color: var(--contrast-text);
		background-color: var(--dark-transparent-overlay);
	}

	.image_for_google_to_read, .background_texture {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	}

	.image_for_google_to_read {
		z-index: -1;
	}

	.product_and_attachment_images_wrapper :global(img) {
		position: absolute;
		width: 100%;
		top: 10%;
	}
</style>
